import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery } from '@mui/material'
import { Stack, useTheme } from '@mui/system'
import InputField from '../../../components/InputFields/InputField'
import SelectField from '../../../components/InputFields/SelectField'
import { Contact, ContactRole, EventDto } from '../../../interfaces/IEvent'
import { FormEvent, useContext, useState } from 'react'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { enqueueSnackbar } from 'notistack'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'

interface AddContactProps {
  viewEvent: EventDto
  onUpdateEventView: (eventId: string) => void
}

export default function AddContact({ viewEvent, onUpdateEventView }: AddContactProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const [openAddContact, setOpenAddContact] = useState<boolean>(false)
  const theme = useTheme()
  const smFullscreen = useMediaQuery(theme.breakpoints.down('sm'))
  const initialContact = {
    id: '',
    name: '',
    phone: '',
    email: '',
    role: ContactRole.Kontaktperson,
  }
  const [contact, setContact] = useState<Contact>(initialContact)
  const [sendLoading, setSendLoading] = useState<boolean>(false)

  const handleOpenAddContact = () => {
    setOpenAddContact(true)
  }

  const handleCloseAddContact = () => {
    setContact(initialContact)
    setOpenAddContact(false)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    setSendLoading(true)

    e.preventDefault()

    const response = await context.restApiClient.buildRequest(`/api/event/${viewEvent.id}/contacts/add`).serializeToJsonContent(contact).putAsync()

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke legge til kontakt. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    } else {
      enqueueSnackbar(<Typography variant="body2">Kontakt lagt til.</Typography>, { variant: 'success' })
      setOpenAddContact(false)
      setContact(initialContact)
      onUpdateEventView(viewEvent.id)
    }

    setSendLoading(false)
  }

  return (
    <>
      <IconButton size="small" color="primary" onClick={handleOpenAddContact}>
        <PersonAddOutlinedIcon />
      </IconButton>
      <Dialog
        open={openAddContact}
        onClose={handleCloseAddContact}
        fullWidth
        maxWidth="xs"
        fullScreen={smFullscreen}
        scroll={'paper'}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'background.default',
          },
        }}
      >
        <DialogTitle>Legg til kontakt</DialogTitle>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'contents',
          }}
        >
          <DialogContent dividers>
            <Stack direction={'column'} spacing={1}>
              <SelectField
                name="role"
                title="Rolle*"
                placeholder="Ingen valgt"
                required={true}
                value={contact.role}
                selectOptions={Object.values(ContactRole)}
                onChange={(e) => setContact({ ...contact, role: e.target.value })}
              />
              <InputField
                name="name"
                title="Navn*"
                required={true}
                size="medium"
                placeholder="Navn"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                maxLength={30}
              />
              <InputField
                name="phone"
                title={ContactRole.Kontaktperson === contact.role ? 'Telefon*' : 'Telefon'}
                required={ContactRole.Kontaktperson === contact.role ? true : false}
                size="medium"
                placeholder="Telefon"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                maxLength={11}
              />
              <InputField
                name="email"
                title="Epost"
                size="medium"
                placeholder="Epost"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                maxLength={50}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack
              direction={'row'}
              spacing={1}
              sx={{
                width: '100%',
                justifyContent: 'right'
              }}>
              <Button variant="contained" color="error" onClick={handleCloseAddContact} disabled={sendLoading}>
                Avbryt
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={sendLoading}
                startIcon={sendLoading ? <LoadingIndicator size={'xsmall'} /> : null}
              >
                Legg til
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
